import { useState, useEffect } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import type { CustomSkill } from "@forgepoint/shared";
import { CUSTOM_SKILL_STATUS } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useCustomSkillsStore } from "@/store/customSkillsStore";
import {
  useAgentWorkflowActionsStore,
  ActionDefinition,
} from "@/store/agentWorkflowActionsStore";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { SelectActionModal } from "@/modules/agents/components/SelectActionModal";
import { CustomSkillSyncBanner } from "./CustomSkillSyncBanner";

interface ToolFormRow {
  key: string;
  actionId: string;
  connectionId: string | null;
}

interface CustomSkillEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skillId?: string | null;
  onSaved?: () => void;
}

const BLANK_FORM = {
  name: "",
  description: "",
  instructions: "",
  isActive: true,
  tools: [] as ToolFormRow[],
};

let toolKeyCounter = 0;
const nextToolKey = () => `tool-${++toolKeyCounter}`;

/** Sheet for creating or editing a custom skill. */
export function CustomSkillEditorSheet({
  open,
  onOpenChange,
  skillId,
  onSaved,
}: CustomSkillEditorSheetProps) {
  const getSkill = useCustomSkillsStore((s) => s.getSkill);
  const createSkill = useCustomSkillsStore((s) => s.createSkill);
  const updateSkill = useCustomSkillsStore((s) => s.updateSkill);

  const fetchActionsByIds = useAgentWorkflowActionsStore(
    (s) => s.fetchActionsByIds,
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [tools, setTools] = useState<ToolFormRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);

  const isEditMode = Boolean(skillId);
  const skill: CustomSkill | undefined = skillId
    ? getSkill(skillId)
    : undefined;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!skillId || !skill) {
      setName(BLANK_FORM.name);
      setDescription(BLANK_FORM.description);
      setInstructions(BLANK_FORM.instructions);
      setIsActive(BLANK_FORM.isActive);
      setTools(BLANK_FORM.tools);
      return;
    }

    setName(skill.name);
    setDescription(skill.description);
    setInstructions(skill.instructions);
    setIsActive(skill.status === CUSTOM_SKILL_STATUS.ACTIVE);

    const toolRows = skill.tools.map((t) => ({
      key: nextToolKey(),
      actionId: t.actionId,
      connectionId: t.connectionId,
    }));
    setTools(toolRows);

    const actionIds = skill.tools.map((t) => t.actionId);
    if (actionIds.length > 0) {
      fetchActionsByIds(actionIds);
    }
  }, [open, skillId, skill, fetchActionsByIds]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setName(BLANK_FORM.name);
      setDescription(BLANK_FORM.description);
      setInstructions(BLANK_FORM.instructions);
      setIsActive(BLANK_FORM.isActive);
      setTools(BLANK_FORM.tools);
    }
    onOpenChange(newOpen);
  };

  const handleActionSelected = (action: ActionDefinition) => {
    setTools((prev) => {
      if (prev.some((t) => t.actionId === action.id)) {
        toast.error("This action has already been added");
        return prev;
      }
      return [
        ...prev,
        { key: nextToolKey(), actionId: action.id, connectionId: null },
      ];
    });
  };

  const handleRemoveTool = (key: string) => {
    setTools((prev) => prev.filter((t) => t.key !== key));
  };

  const handleToolConnectionChange = (key: string, connectionId: string) => {
    setTools((prev) =>
      prev.map((t) => (t.key === key ? { ...t, connectionId } : t)),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(name.trim())) {
      toast.error(
        "Name must only contain letters, numbers, hyphens, and underscores",
      );
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    if (!instructions.trim()) {
      toast.error("Instructions are required");
      return;
    }

    const toolsPayload = tools
      .filter((t) => t.actionId)
      .map((t) => ({
        actionId: t.actionId,
        connectionId: t.connectionId,
      }));

    setIsSaving(true);
    try {
      if (isEditMode && skillId) {
        const result = await updateSkill(skillId, {
          name: name.trim(),
          description: description.trim(),
          instructions: instructions.trim(),
          status: isActive
            ? CUSTOM_SKILL_STATUS.ACTIVE
            : CUSTOM_SKILL_STATUS.DISABLED,
          tools: toolsPayload,
        });
        if (result) {
          toast.success("Skill updated");
          onOpenChange(false);
          onSaved?.();
        } else {
          toast.error("Failed to update skill");
        }
      } else {
        const result = await createSkill({
          name: name.trim(),
          description: description.trim(),
          instructions: instructions.trim(),
          tools: toolsPayload,
        });
        if (result) {
          toast.success("Skill created");
          onOpenChange(false);
          onSaved?.();
        } else {
          toast.error("Failed to create skill");
        }
      }
    } catch {
      toast.error(
        isEditMode ? "Failed to update skill" : "Failed to create skill",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const title = isEditMode ? "Edit Custom Skill" : "Create Custom Skill";
  const saveButtonText = isEditMode ? "Save Changes" : "Create Skill";
  const isSystemManaged = Boolean(skill?.syncVersion);

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full sm:w-[660px] sm:max-w-[660px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>
              {isSystemManaged
                ? "Configured automatically. Manual edits made here will be overwritten on the next system update."
                : "Define a skill with instructions and tools for the agent."}
            </SheetDescription>
            {isSystemManaged && skill && (
              <div className="pt-1">
                <CustomSkillSyncBanner
                  syncVersion={skill.syncVersion}
                  syncUpdatedBy={skill.syncUpdatedBy}
                  updatedAt={skill.updatedAt}
                />
              </div>
            )}
          </SheetHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="skill-name">Name *</Label>
              <Input
                id="skill-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. sharepoint-file-manager"
                maxLength={100}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="skill-description">Description *</Label>
              <Textarea
                id="skill-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this skill does and when it should be used"
                rows={2}
                maxLength={500}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="skill-instructions">Instructions *</Label>
              <Textarea
                id="skill-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Detailed instructions for the agent on how and when to use this skill..."
                rows={8}
              />
            </div>

            {isEditMode && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="skill-active">Active</Label>
                  <div className="text-sm text-muted-foreground">
                    Disabled skills are not loaded by the agent
                  </div>
                </div>
                <Switch
                  id="skill-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            )}

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Tools</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setActionModalOpen(true)}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add Tool
                </Button>
              </div>

              {tools.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No tools added. The skill will be instructions-only.
                </p>
              )}

              {tools.map((tool) => (
                <ToolRow
                  key={tool.key}
                  tool={tool}
                  onConnectionChange={(connectionId) =>
                    handleToolConnectionChange(tool.key, connectionId)
                  }
                  onRemove={() => handleRemoveTool(tool.key)}
                />
              ))}
            </div>
          </div>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline" disabled={isSaving}>
                Cancel
              </Button>
            </SheetClose>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saveButtonText}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <SelectActionModal
        open={actionModalOpen}
        onOpenChange={setActionModalOpen}
        onSelectAction={handleActionSelected}
        filterTypes={["ACTION"]}
      />
    </>
  );
}

interface ToolRowProps {
  tool: ToolFormRow;
  onConnectionChange: (connectionId: string) => void;
  onRemove: () => void;
}

function ToolRow({ tool, onConnectionChange, onRemove }: ToolRowProps) {
  const action = useAgentWorkflowActionsStore((s) =>
    tool.actionId ? s.actions.get(tool.actionId) : undefined,
  );
  const connectionConfig = action?.appConnectionConfig;

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            {action?.imageUrl && (
              <img
                src={action.imageUrl}
                alt={action.name}
                className="h-5 w-5 rounded object-contain"
              />
            )}
            <span className="text-sm font-medium">
              {action?.name ?? tool.actionId}
            </span>
          </div>

          {connectionConfig && (
            <div className="grid gap-1">
              <Label className="text-xs">Connection</Label>
              <ConnectionSelector
                appId={connectionConfig.appId}
                providerId={connectionConfig.providerId}
                connectionId={tool.connectionId}
                onSelect={onConnectionChange}
              />
            </div>
          )}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
